import Box from '@/common/components/Box';
import Select from '@/common/components/Select';
import { Mapper } from '@/common/services/mapper';
import { CompanyBranchData } from '@/common/types/companyBranches.type';
import { InfoIcon, SearchIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import GovernmentAllocationDistributionBranchItem from './GovernmentAllocationDistributionBranchItem';
import { useGovernmentAllocationsDistributions } from '@/features/privateCompanies/hooks/useGovernmentAllocationsDistributions';
import { CompanyGovernmentAllocationDistribution, ControlledByType, GovernmentAllocationMovementsKind } from '@/features/privateCompanies/types/companyGovernmentAllocation.type';
import { useRef, useState } from 'react';
import GovernmentAllocationDistributionCreateModal from './GovernmentAllocationDistribution/CreateDistributionModal';
import { GovernmentAllocationDistributionShowModal } from './GovernmentAllocationDistribution/ShowDistributionModal';
import { useGovernmentAllocationsBranchesToDistribute } from '@/features/privateCompanies/hooks/useGovernmentAllocationsBranchesToDistribute';
import TextField from '@/common/components/TextField';
import useDebounce from '@/common/hooks/useDebounce';
import { Option } from '@/common/types/option';

interface GovernmentAllocationDistributionProps {
  controlledBy?: ControlledByType;
  movementsKind?: GovernmentAllocationMovementsKind;
}

export const GovernmentAllocationDistribution = ({
  controlledBy = 'currency',
  movementsKind = 'common',
}: GovernmentAllocationDistributionProps) => {
  const { id } = useParams();

  const governmentAllocationDistributionsContainerRef = useRef<HTMLDivElement>(null);

  const [companyBranch, setCompanyBranch] = useState<CompanyBranchData>();
  const [distribution, setDistribution] = useState<CompanyGovernmentAllocationDistribution>();

  const [searchBranch, setSearchBranch] = useDebounce('');
  const [searchGovernmentAllocationsDistributions, setSearchGovernmentAllocationsDistributions] = useDebounce('');

  const { data: branchesToDistributeOptions, meta: branchesToDistributeMeta } = useGovernmentAllocationsBranchesToDistribute({
    filters: {
      governmentAllocationId: id,
      search: searchBranch,
      pageSize: 9999,
    },
    mapper: Mapper.mapToOptionsWithItem<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const {
    data: governmentAllocationDistributions,
    meta: governmentAllocationDistributionsMeta,
    isFetching: isFetchingGovernmentAllocationDistributions,
    fetchNextPage: fetchGovernmentAllocationDistributionsNextPage,
  } = useGovernmentAllocationsDistributions<CompanyGovernmentAllocationDistribution[]>({
    governmentAllocationId: id,
    filters: {
      search: searchGovernmentAllocationsDistributions,
    },
  });

  const shouldFetchNextPage = () => {
    if (
      !governmentAllocationDistributionsContainerRef.current ||
      governmentAllocationDistributionsMeta.current_page >= governmentAllocationDistributionsMeta.total_pages ||
      isFetchingGovernmentAllocationDistributions
    ) {
      return false;
    }

    const { scrollTop, scrollHeight, clientHeight } = governmentAllocationDistributionsContainerRef.current;
    return scrollTop + clientHeight >= scrollHeight - 16;
  };

  const handleScroll = () => {
    const hasNextPage = shouldFetchNextPage();

    if (hasNextPage) {
      fetchGovernmentAllocationDistributionsNextPage();
    }
  };

  return (
    <>
      <Box className="flex flex-col gap-4">
        <p>Divisão de crédito do empenho</p>

        <div className="flex gap-x-2 items-center">
          <InfoIcon strokeWidth={1} className="text-yellow-600" />

          <div className="flex flex-col gap-1">
            <p className="font-bold text-xs leading-3">
              Você poderá dividir o {controlledBy === 'quantity' ? 'quantidade empenhada' : 'valor empenhado'} para cada Departamento associado incluindo essa filial.
            </p>
            <p className="font-light text-[10px] italic leading-3">
              Caso um departamento não seja adicionado aqui, o mesmo não terá crédito.
            </p>
          </div>
        </div>

        <Select
          placeholder={
            <div className="flex items-center gap-2">
              <SearchIcon strokeWidth={1} />
              <p>Pesquisar departamentos</p>
            </div>
          }
          onInputChange={setSearchBranch}
          options={branchesToDistributeOptions || []}
          value={''}
          onChange={(newValue) => {
            const { item, value } = newValue as Option & { item: CompanyBranchData };
            const hasAlreadyAdded = governmentAllocationDistributions?.some(({ branch }) => branch.id === value)

            if (hasAlreadyAdded) {
              const distribution = governmentAllocationDistributions.find(({ branch }) => branch.id === value)
              return setDistribution(distribution)
            }

            setCompanyBranch(item)
          }}
        />

        <p className="text-sm">
          {governmentAllocationDistributionsMeta.total_distributions_count}/
          {governmentAllocationDistributionsMeta.total_distributions_count + branchesToDistributeMeta.total}{' '}
          Departamentos adicionados
        </p>

        <Box className="flex flex-col gap-y-4 p-4">
          <TextField
            LeftIcon={<SearchIcon strokeWidth={1} />}
            placeholder="Pesquisar"
            onChange={(e) => {
              setSearchGovernmentAllocationsDistributions((e.target as HTMLInputElement).value);
            }}
          />

          {!!governmentAllocationDistributions?.length && (
            <div
              ref={governmentAllocationDistributionsContainerRef}
              onScroll={handleScroll}
              className="flex max-h-[calc(100vh-496px)] flex-col gap-4 overflow-y-scroll thin-scroll"
            >
              {governmentAllocationDistributions?.map((distribution) => (
                <GovernmentAllocationDistributionBranchItem
                  key={distribution.id}
                  distribution={distribution}
                  controlledBy={controlledBy}
                  movementsKind={movementsKind}
                />
              ))}
            </div>
          )}
        </Box>
      </Box>

      {!!distribution && (
        <GovernmentAllocationDistributionShowModal
          distribution={distribution}
          setDistribution={setDistribution}
          controlledBy={controlledBy}
        />
      )}

      <GovernmentAllocationDistributionCreateModal
        companyBranch={companyBranch}
        setCompanyBranch={setCompanyBranch}
        governmentAllocationId={id}
        distribution={governmentAllocationDistributions?.find((distribution) => distribution.branch.id === companyBranch?.id)!}
        controlledBy={controlledBy}
        movementsKind={movementsKind}
      />
    </>
  );
};
